import { Button, Tooltip } from '@toggle/design-system';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TokenUsageCircle } from '~/components/chat/components/token-usage-circle/TokenUsageCircle';
import { clampTokenUsagePercentage } from '~/components/chat/components/token-usage-utils';

import styles from './TokenUsageControl.module.scss';
import { TokenUsagePopover } from './TokenUsagePopover';

export interface TokenUsageControlProps {
  percentage: number;
  resetDate?: string;
}

export const TokenUsageControl = ({
  percentage,
  resetDate,
}: TokenUsageControlProps) => {
  const { t } = useTranslation('chat');
  const [isHovered, setIsHovered] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const clampedPercentage = clampTokenUsagePercentage(percentage);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleClick = () => {
    setIsPopoverOpen(isOpen => !isOpen);
  };

  const handleClose = () => {
    setIsPopoverOpen(false);
    setIsHovered(false);
  };

  const percentageLabel = t('chat:tokenUsage.percentageLabel', {
    percentage: clampedPercentage,
  });
  const tooltipClassName = isPopoverOpen
    ? clsx(styles.popoverBubble, clampedPercentage >= 90 && styles.widePopover)
    : undefined;

  return (
    <Tooltip
      className={tooltipClassName}
      inPortal
      hasArrow={!isPopoverOpen}
      hasMaxWidth={!isPopoverOpen}
      gap={isPopoverOpen ? 0 : undefined}
      isOpen={isHovered || isPopoverOpen}
      label={
        isPopoverOpen ? (
          <TokenUsagePopover
            percentage={clampedPercentage}
            resetDate={resetDate}
          />
        ) : (
          percentageLabel
        )
      }
      onClose={handleClose}
      placement={isPopoverOpen ? 'bottom' : 'top'}
      trigger={[]}
    >
      <Button
        type="button"
        variant="empty"
        size="medium"
        className={styles.control}
        aria-label={t('chat:tokenUsage.controlLabel', {
          percentage: clampedPercentage,
        })}
        aria-expanded={isPopoverOpen}
        data-testid="token-usage-control"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <TokenUsageCircle
          percentage={clampedPercentage}
          ariaLabel={percentageLabel}
        />
      </Button>
    </Tooltip>
  );
};
